import {
  Box,
  CardList,
  CardListRow,
  CardListItem,
  CardListActions,
  CardListText,
  CardListMedia,
  CardMedia,
  Button,
  Stack,
  Fixed,
  Text,
  Inline,
  SearchField,
  FilterMenu,
  FormControlLabel,
  Checkbox,
  FilterMenuOption,
  LoadingLayout,
  Backdrop,
  IconButton,
  Center
} from "@digitalreality/ui";
import { CalendarIcon, LayersFilledIcon, LinkIcon, NotesCustomIcon, ReportIcon, SettingsIcon, TrashCanIcon, OverflowMenuVerticalIcon } from "@digitalreality/ui-icons";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ClientDetailsModal from "./ClientDetailsModal";
import ClientJobsModal from "./ClientJobsModal";
import Divider from '@mui/material/Divider';
import React from "react";
import { useAppState } from "../../StateStore/Store";
import {
  filterProjects,
  getProjectsList,
} from "../../StateStore/ProjectList/ProjectListActionMiddleware";
import {
  setFilter,
  setSearchTerm,
} from "../../StateStore/ProjectList/ProjectListActions";
import {
  FilterOptions,
  LicenseCodes,
  LicenseCodesAddOns,
} from "../../Helpers/Constants";
import {
  getEmptyProjectData,
  getProjectDetailsForModal,
} from "../../StateStore/ProjectModal/ProjectModalActionMiddleware";
import { clearProjectDetails } from "../../StateStore/ProjectModal/ProjectModalActions";
import "./../../App.css";
import { pangeaUIColors } from "../../Helpers/Colors";
import { License } from "../../Models/License";
import dayjs from "dayjs";
import DeleteProjectModal from "./DeleteProjectModal";
import { getProjectJobsStatus } from "../../StateStore/ProjectJobsStatusModal/ProjectJobsStatusModalActionMiddleware";
import Typography from '@mui/material/Typography';

const logoPlaceholder = require("../../Images/noImage.png");
const errorImage = require("../../Images/errorImage.png");

export type ProjectJobsStatus = {
  projectId?: number;
  errorCode?: string;
};

export default function ClientsList() {
    const { t } = useTranslation();
    const { state, dispatch } = useAppState();
    const [openModal, setOpen] = useState(false);
    const [showJobsModal, setShowJobsModalState] = useState(false);
    const [jobsStatusData, setJobsStatusData] = useState<ProjectJobsStatus | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [deleteProjectModal, setDeleteProjectModal] = useState({
        open: false,
        projectName: "",
    });
    const [hideExpired, setHideExpired] = useState(false);
    const [showExpiringSoon, setShowExpiringSoon] = useState(false);

    var noExpiringSoonProjects: boolean = true;

    const filterOptions = [
    {
        id: FilterOptions.Customer,
        label: t("Texts.Customer"),
    },
    {
        id: FilterOptions.Name,
        label: t("Texts.Domain"),
    },
    {
        id: FilterOptions.Country,
        label: t("Texts.Country"),
    },
    {
        id: FilterOptions.LicenseExpiration,
        label: t("Texts.LicenseExpirationDate"),
    },
    ];

    useEffect(() => {
        getProjectsList(FilterOptions.Customer)(dispatch);
    }, []);

    const handleModalOpen = (projectId?: number) => {
        dispatch(clearProjectDetails());
        if (projectId && projectId > 0) {
            getProjectDetailsForModal(projectId)(dispatch);
            setEditMode(true);
            setOpen(true);
        } else {
            getEmptyProjectData()(dispatch);
            setEditMode(false);
            setOpen(true);
        }
    };

    const handleOpenJobsPanel = (projectId: number, projectTimezoneOffset: number) => {
        // Cleanup the current jobs state and get the initial data
        state.projectJobsStatusModal.projectId = projectId;
        state.projectJobsStatusModal.projectTimezoneOffset = projectTimezoneOffset;
        state.projectJobsStatusModal.data = null;
        getProjectJobsStatus(projectId)(dispatch);

        // Display the modal window
        setShowJobsModalState(true);
    };

    const handleFilter = (filerOptions: FilterMenuOption[]) => {
        if (filerOptions.length !== 0) {
            const filter = filerOptions[0].id as FilterOptions;
            const filteredProjects = filterProjects(state.projectList.projectsList, filter);
            dispatch(setFilter(filter, filteredProjects));
        }
    };

    const handleSearch = (searchString: string) => {
        console.log("search string:" + searchString);
        if (searchString) {
            const searchProjects = applySearch(searchString);
            dispatch(setSearchTerm(searchString, searchProjects));
        } else {
            dispatch(setSearchTerm(undefined, state.projectList.originalProjectList));
        }
    };

    const applySearch = (searchTerm: string) => {
        return state.projectList.originalProjectList ? state.projectList.originalProjectList.filter((client) => 
            client.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
            client.customer?.toLowerCase().includes(searchTerm.toLowerCase()) || 
            t("Texts.c" + client.country).toLowerCase().includes(searchTerm.toLowerCase())) : [];
    };

    const isEntitlementDowngrade = (current: string[], next: string[]): boolean => {
        // Core without addons, OEMLegacy and Trial Basic are at the same level
        const isBaseLevel = (codes: string[]): boolean => (codes.includes("Core") && codes.length === 1) || codes.includes("OEMLegacy") || codes.includes("TrialBasic");

        if (isBaseLevel(current) && isBaseLevel(next)) {
            return false;
        }

        // Expand "Trial" to its equivalent set of codes
        const expandCodes = (codes: string[]): string[] => codes.includes("Trial") ? ["Core", "SnowManagement", "TaskManager"] : codes;

        const currentCodes = expandCodes(current);
        const nextCodes = expandCodes(next);

        // Check if there is a difference between the addons
        return currentCodes.some(code => !nextCodes.includes(code));
    }

    const setLicense = (licenses: License[]) => {
        const noLicense = t("Texts.NoLicense");
        var expirationDate = "";
        var expirationDateText = "";
        var licenseCodes: string[] = [];
        var addOns: string[] = [];

        licenses.map((license) => {
            if (Object.keys(LicenseCodes).includes(license.code)) {
                if (Object.keys(LicenseCodesAddOns).includes(license.code)) {
                    addOns.push(license.code);
                } else {
                    licenseCodes.push(license.code);
                }
            }

            if (license.licensekey == "legacy") {
                licenseCodes.push(license.code);
                expirationDate = license.enddate;
            }
        });

        var currentCodes: string[] = [];
        var currentKey: string = "";
        var currentEndDate: string = "";
        var nextCodes: string[] = [];
        var nextKey: string = "";
        var nextEndDate: string = "";
        licenses.map((license) => {
            if (currentKey === "") {
                currentKey = license.licensekey;
                currentEndDate = license.enddate;
            }
            else 
            {
                if (license.licensekey !== currentKey && nextKey === "") {
                    nextKey = license.licensekey;
                    nextEndDate = license.enddate;
                }
            }

            if (license.licensekey === currentKey) {
                currentCodes.push(license.code);
            }
            if (license.licensekey === nextKey) {
                nextCodes.push(license.code);
            }
        });

        // We have only one valid license
        if (nextKey === "") {
            expirationDate = currentEndDate;
            expirationDateText = t("Texts.ExpirationDate") + ": " + dayjs(currentEndDate).format("DD MMM YYYY")
        } else {
            if (isEntitlementDowngrade(currentCodes, nextCodes)) {
                expirationDate = currentEndDate;
                expirationDateText = t("License downgrade on") + ": " + dayjs(currentEndDate).format("DD MMM YYYY")
            } else {
                expirationDate = nextEndDate;
                expirationDateText = t("Texts.ExpirationDate") + ": " + dayjs(nextEndDate).format("DD MMM YYYY")
            }
        }

        if (expirationDate !== "" || licenseCodes.length > 0 || addOns.length > 0) {
            // If the list only contains "OEMLegacy", show "OEMLegacy".
            // If the list contains "OEMLegacy" and others, then hide "OEMLegacy" and only show the other codes (unique, joined).
            var licenseCodesText = "";
            if (licenseCodes.length === 1 && licenseCodes[0] === "OEMLegacy") {
                licenseCodesText = "OEMLegacy";
            } else {
                licenseCodesText = [...new Set(licenseCodes)].filter(code => code !== "OEMLegacy").join(", ");
            }

            return (
                <Box style={{ padding: 0, maxHeight: '80px', overflowY: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        {licenseCodes.length == 1 && licenseCodes[0] == "OEMLegacy" ? 
                            (<Typography color={"green"} style={{ fontSize: '12px', fontWeight: "400" }}>{t("Texts.ExpirationDate") + ": Unlimited"}</Typography> ) :
                            (<Typography color={dayjs(expirationDate).isBefore(dayjs(), "day") ? "red" : dayjs(expirationDate).isBefore(dayjs().add(30, "day"), "day") ? "yellow" : "green"} style={{ fontSize: '12px', fontWeight: "400" }}>{expirationDateText}</Typography>)
                        }
                        <Typography style={{ fontSize: '14px', fontWeight: "600" }}>{licenseCodesText}</Typography>
                        <Typography style={{ fontSize: '13px', fontWeight: "400" }} color={"#999999"}>{[...new Set(addOns)].join(", ")}</Typography>
                    </div>
                </Box>
            );
        }

        return noLicense;
    };

    const expired = (licenses?: License[]) => {
        return (!licenses || !licenses?.[0].enddate || dayjs(licenses?.[0].enddate).isBefore(dayjs(), "day"));
    };

    const expiringSoon = (licenses?: License[]) => {
        const expiring = dayjs(licenses?.[0].enddate).isAfter(dayjs()) && dayjs(licenses?.[0].enddate).isBefore(dayjs().add(30, "days"), "day");
        if (expiring) {
            // check if to show "No Projects" or expiring projects list
            noExpiringSoonProjects = false;
        }
        return expiring;
    };

    const ErrorMessageBlock = (
        <Box>
            <Center marginTop="130px">
                <Text variant="title"> {t("Texts.Error") + "!"} </Text>
                <img src={errorImage} alt="" />
                <Text variant="body1">
                    {state.projectList.errorCode ? t("Texts." + state.projectList.errorCode) : ""}
                </Text>
            </Center>
        </Box>
    );
    const EmptyProjectListBlock = (
        <Box>
            <Center marginTop="130px">
                {" "}
                <Text> {t("Texts.NoProjects")}</Text>
            </Center>
        </Box>
    );

    const ProjectsCardListBlock = (
        <CardList className="cardList">
            {state.projectList.projectsList?.map((project) => {
                var imageUrl: any;
                if (project.logo) {
                    imageUrl = "data:image/png;base64," + project.logo;
                } else {
                    imageUrl = logoPlaceholder;
                }
                return ((hideExpired && expired(project.licenses)) || (showExpiringSoon && !expiringSoon(project.licenses)) || (
                    <CardListRow
                        sx={{height: "96px", cursor: "default"}}
                        key={project.id}
                        className="cardListRow">
                        <CardListMedia xs={1} className="cardListMedia">
                            <CardMedia
                                sx={{
                                    height: "84px",
                                    maxWidth: "112px",
                                    maxHeight: "84px",
                                    borderRadius: "6px",
                                    background: project.logo ? "white" : "#4C4C4C",
                                    objectFit: "scale-down",
                                }}
                                component="img"
                                src={imageUrl}/>
                        </CardListMedia>
                        <CardListItem xs={2} style={{ paddingTop: 0 }}>
                            <Typography overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" style={{ fontSize: '14px', fontWeight: "600" }}>{project.customer}</Typography>
                            <Text variant="caption" color="#999999">{project.name}</Text>
                            <Text variant="caption" color="#999999">{project.country && project.country.toLowerCase() !== "undefined" ? t("Texts.c" + project.country) : "Undefined " + t("Texts.Country")}</Text>
                        </CardListItem>

                        <CardListItem xs={3} style={{ paddingTop: 0 }}> {project.licenses ? setLicense(project.licenses) : t("Texts.NoLicense")}</CardListItem>
                        
                        <IconButton
                            sx={{ marginRight: "0px" }}
                            onClick={() => handleOpenJobsPanel(project.id, project.timezoneOffset)}
                            disabled={!project.tags?.includes("#UseNewJobsWorker")}><CalendarIcon />
                        </IconButton>
                        <CardListItem xs={2} style={{ paddingTop: 0 }}>
                            {!project.tags?.includes("#UseNewJobsWorker") ? 
                            (
                                <Text variant="caption" color="#999999">{t("Project is not migrated to the new data importer")}</Text>
                            ) : project.lastJobRun ? 
                            (
                                <Box>
                                <Text variant="caption" color="#999999" style={{ fontSize: '12px', fontWeight: "400" }}>{t("Texts.DailyDataLastQuery")}</Text>
                                <Text variant="caption" color="#ffffff" style={{ fontSize: '12px', fontWeight: "400" }}>{project.lastJobRun ? dayjs(project.lastJobRun).utc().format("DD MMM YYYY HH:mm") : ""}</Text>
                                </Box>
                            ) : 
                            (
                                <Text variant="caption" color="#999999">{t("Texts.NoJobsRun")}</Text>
                            )}
                        </CardListItem>
                        <CardListActions xs justifyContent="flex-end" pr="8px" style={{ paddingTop: 0 }}>
                            <Button
                                variant="contained"
                                sx={{
                                    marginTop: "4px",
                                    height: "32px",
                                    fontSize: "14px",
                                    lineHeight: "20px",
                                    marginRight: "24px",
                                    color: pangeaUIColors.grey[900],
                                }}
                                startIcon={<LinkIcon />}
                                onClick={() => window.open(`${process.env.REACT_APP_ALPINE_PRO_BASE_URL}/${project.name}`, "_blank")}>
                                {t("Texts.OpenProject")}
                            </Button>
                            <IconButton
                                sx={{ marginRight: "16px" }}
                                onClick={() => handleModalOpen(project.id)}>
                                <SettingsIcon />
                            </IconButton>
                            <IconButton
                                sx={{ marginRight: "16px" }}
                                disabled={true}
                                onClick={() => {setDeleteProjectModal({open: true,projectName: project.name})}}>
                                <TrashCanIcon />
                            </IconButton>
                        </CardListActions>
                    </CardListRow>
                ));
            })}
        </CardList>
    );

    return (
        <Box width="calc(100vw - 70px)">
            <Stack height="140px">
                <Fixed
                    top={0}
                    sx={{ width: "calc(100% - 56px)", zIndex: 5, height: "inherit" }}
                    bgcolor="background.paper">
                    <Stack
                        gap={2}
                        sx={{
                            marginLeft: "75px",
                            marginRight: "40px",
                            marginTop: "30px",
                        }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between">
                            <Text className="titleLarge">{t("Texts.Projects")}</Text>
                            <Inline gap={3}>
                                <SearchField
                                    placeholder={t("Texts.SearchText")}
                                    defaultValue=""
                                    width={240}
                                    size="small"
                                    onSearch={handleSearch}/>
                                <Button
                                    variant="accent"
                                    size="large"
                                    sx={{ color: pangeaUIColors.grey[900] }}
                                    onClick={() => handleModalOpen()}>
                                    {t("Texts.CreateProject")}
                                </Button>
                            </Inline>
                        </Stack>
                        <Stack direction="row" justifyContent="flex-end" gap={8}>
                            <FilterMenu
                                sx={{ fontSize: "12px" }}
                                optionsSelected={filterOptions.filter((f) => f.id === state.projectList.filter)}
                                onSelect={handleFilter}
                                options={filterOptions}
                                title={t("Texts.SortBy")}/>
                            <FormControlLabel
                                control={<Checkbox onChange={() => setHideExpired(!hideExpired)} />}
                                sx={{ fontSize: "12px" }}
                                label={t("Texts.HideExpiredProjects")}/>
                            <FormControlLabel
                                control={<Checkbox onChange={() => setShowExpiringSoon(!showExpiringSoon)}/>}
                                sx={{ fontSize: "12px" }}
                                label={t("Texts.LicenseExpiringSoon")}/>
                        </Stack>
                    </Stack>
                </Fixed>
            </Stack>
            {state.projectList.errorCode != null ? ErrorMessageBlock : state.projectList.projectsList?.length === 0 || (showExpiringSoon && noExpiringSoonProjects) ? EmptyProjectListBlock : ProjectsCardListBlock}
            <ClientDetailsModal
                showModal={openModal}
                setOpenModal={setOpen}
                editMode={editMode}>
            </ClientDetailsModal>
            <ClientJobsModal
                showModal={showJobsModal}
                setShowModalState={setShowJobsModalState}
                setJobsStatusDataState={setJobsStatusData}>
            </ClientJobsModal>
            <DeleteProjectModal
                open={deleteProjectModal.open}
                projectName={deleteProjectModal.projectName}
                setState={setDeleteProjectModal}></DeleteProjectModal>
            <Backdrop open={!state.projectList.loaded} sx={{ zIndex: 10 }}>
                <LoadingLayout />
            </Backdrop>
        </Box>
    );
}
